/**
 * Reading the one line the customer pasted.
 *
 * ## One required attribute, and the rest have defaults that are correct
 *
 * The founder's rule for this product is that an integration asking for three
 * things is an integration nobody finishes. So the tag carries a key and
 * nothing else:
 *
 * ```html
 * <script defer src="https://panel.example/v1/p.js" data-panel-key="pk_live_…"></script>
 * ```
 *
 * Every other attribute below exists for a case a customer will actually hit,
 * and every one of them has a default that is right for the customer who never
 * reads this list.
 *
 * ## Where the events go is NOT an attribute
 *
 * The endpoint is derived from the script's own `src`, which is the address the
 * customer already typed. Asking for it twice would be asking them to keep two
 * strings in agreement for ever, and the failure when they drift is silent: the
 * script loads from one host and posts to a host that answers 404, so the
 * install looks perfect and measures nothing.
 *
 * `data-panel-host` overrides it for the one case that cannot derive: a
 * customer who reverse-proxies `/v1/p.js` through their own domain — which is a
 * legitimate thing to do for a first-party install, and which makes the script
 * arrive from their origin while the events still have to reach ours.
 */
import { PUBLIC_INGEST_PATH, PUBLIC_SCRIPT_KEY_ATTRIBUTE } from '@panel/shared/tracker';

export interface TrackerConfig {
  /** `pk_live_…`, from `data-panel-key`. Public by design — see the dashboard's snippet panel. */
  readonly key: string;
  /** Absolute URL the events are POSTed to. */
  readonly endpoint: string;
  /** Send a page view on load and follow single-page navigations. `data-panel-auto="false"` turns both off. */
  readonly auto: boolean;
  /** Count even on a development host. `data-panel-local="true"`. */
  readonly countLocal: boolean;
  /** Report the page's other scripts once per load. `data-panel-diagnostics="false"` turns it off. */
  readonly diagnostics: boolean;
  /**
   * Read the five `utm_*` parameters out of the URL. `data-panel-utm="false"`
   * turns it off.
   *
   * On by default, and that default is the one decision on this list that had
   * to be argued rather than assumed, because the rest of this script's
   * relationship with the query string is "none". The argument is in
   * `@panel/shared/tracker` at `UTM_QUERY_PARAMS`: five literal names, written
   * by the customer into links the customer publishes. The attribute exists
   * because "argued" is not "settled for everybody", and a customer who wants
   * nothing at all out of their query strings should not have to fork the
   * script to get it.
   */
  readonly utm: boolean;
  /**
   * Count clicks that leave the site, clicks on files, and pages the server
   * answered 404 for. `data-panel-links="false"` turns all three off.
   *
   * One attribute for three measurements rather than three, because they are
   * one decision: each is a fact about a LINK on the customer's own page, none
   * needs an identifier, and a customer who is uneasy about one of them is
   * uneasy about the shape of all three. Three attributes would have been three
   * things to explain for a distinction nobody makes.
   *
   * On by default for the same reason the page view is: a measurement that has
   * to be switched on is a measurement that is off on every site whose owner
   * never read the documentation, and the whole of what leaves the page here is
   * a hostname the customer typed into their own markup.
   */
  readonly links: boolean;
}

const ATTR = {
  host: 'data-panel-host',
  auto: 'data-panel-auto',
  local: 'data-panel-local',
  diagnostics: 'data-panel-diagnostics',
  utm: 'data-panel-utm',
  links: 'data-panel-links',
} as const;

/**
 * `false`, and only `false`.
 *
 * An unknown value means the customer typed something we do not understand, and
 * the safe reading of that is "they did not mean to turn the feature off" —
 * a typo in `data-panel-auto` must not silently stop the product measuring.
 */
function isOff(el: Element, name: string): boolean {
  return (el.getAttribute(name) || '').toLowerCase() === 'false';
}

function isOn(el: Element, name: string): boolean {
  return (el.getAttribute(name) || '').toLowerCase() === 'true';
}

/**
 * Build the config, or return null when there is nothing to build.
 *
 * Null is returned for exactly one reason — no key — and the script then does
 * nothing at all, quietly. It is tempting to `console.warn` here, and it is the
 * wrong call twice over: the message lands in a stranger's console on a page
 * whose owner may never look at it, and the person who needs to hear it is the
 * customer, who will hear it from a dashboard that says "no data received" and
 * can act on it.
 */
export function readConfig(script: Element | null, loc: Location): TrackerConfig | null {
  if (!script) return null;

  const key = (script.getAttribute(PUBLIC_SCRIPT_KEY_ATTRIBUTE) || '').trim();
  if (!key) return null;

  const override = (script.getAttribute(ATTR.host) || '').trim();
  const src = (script as HTMLScriptElement).src || '';
  let endpoint: string;
  try {
    // `new URL(path, base)` rather than string concatenation, so a host given
    // with a trailing slash, with a path, or with no scheme fails here — where
    // it produces no requests — instead of producing a stream of POSTs to a
    // malformed address.
    endpoint = new URL(PUBLIC_INGEST_PATH, override || src || loc.href).toString();
  } catch {
    return null;
  }

  return {
    key,
    endpoint,
    auto: !isOff(script, ATTR.auto),
    countLocal: isOn(script, ATTR.local),
    diagnostics: !isOff(script, ATTR.diagnostics),
    utm: !isOff(script, ATTR.utm),
    links: !isOff(script, ATTR.links),
  };
}

/**
 * Which `<script>` is us.
 *
 * `document.currentScript` is the right answer and is populated for a classic
 * script while it executes — including a `defer`red one, which is what the
 * snippet uses. The fallback exists for the case that breaks it: a tag manager
 * or a bundler that fetches this file and evaluates it out of band, where
 * `currentScript` is null and the only way back to the tag is to look for it.
 * Querying on the key attribute rather than on the `src` is what makes that
 * fallback work when the file has been re-hosted under another name.
 */
export function findScript(doc: Document): Element | null {
  const current = doc.currentScript;
  if (current && current.getAttribute(PUBLIC_SCRIPT_KEY_ATTRIBUTE)) return current;
  return doc.querySelector('script[' + PUBLIC_SCRIPT_KEY_ATTRIBUTE + ']');
}
